import apiClient from './apiClient';
import { toCanonicalTimeframe } from '../utils/timeframes';

// GET /cycles/windows?symbols= takes at most this many (SPT DWLF-309).
const WINDOWS_BATCH_MAX = 50;

// Fetch the live state machine's per-(symbol, timeframe, side)
// inter-pivot gap buffer + computed q25/q50/q75 quantiles. The chart's
// "expected window" overlay consumes this so the shaded region matches
// the band the backend uses to gate cycle.{side}.window.{early,mid,late}
// events. Falls back to null per side when the state row is missing
// (fresh symbol, no backfill yet) — caller decides what to render.
//
// `side` is optional; omitting it returns both lows and highs in the
// same response, which is the typical chart-overlay shape.
export const cyclesAPI = {
  getState: async ({ symbol, timeframe, side } = {}) => {
    if (!symbol || !timeframe) {
      throw new Error('cyclesAPI.getState: symbol and timeframe are required');
    }
    const params = new URLSearchParams();
    params.append('symbol', symbol);
    params.append('timeframe', toCanonicalTimeframe(timeframe));
    if (side) {
      params.append('side', side);
    }
    const response = await apiClient.get(`/cycles/state?${params.toString()}`);
    return response.data;
  },

  // The stored cycle WINDOW per side (DWLF-113 ①): dated opens / centre /
  // closes / hardMax folded from the confirmed-pivot ledger, the gaps it was
  // drawn from, calibration, the standing verification label, provenance.
  // Envelope: { symbol, timeframe, generation, generationSource, verification,
  //             low:  { symbolUsed, current, retiredAbove, history } | null,
  //             high: { … } | null }
  // A side is null when the store holds nothing for that key; `current` is
  // the window the chart draws (null when only retired rows exist). The
  // chart shows an explicit "no window" state for both, never a substitute.
  // Batch form (SPT DWLF-309): GET /cycles/windows?symbols=A,B,C → { …, results: [{ symbol, requested,
  // low, high, error? }] }. The API takes up to 50 symbols a request, so a longer list goes in
  // chunks of 50 and the results are joined (the envelope is the first chunk's).
  getWindowsBatch: async ({ symbols, timeframe, side, depth } = {}) => {
    const read = async (chunk) => {
      const params = new URLSearchParams();
      params.append('symbols', chunk.join(','));
      params.append('timeframe', toCanonicalTimeframe(timeframe));
      if (side) params.append('side', side);
      // DWLF-416: each window's derived price band.
      if (depth) params.append('depth', '1');
      const response = await apiClient.get(`/cycles/windows?${params.toString()}`);
      return response.data;
    };
    const chunks = [];
    for (let i = 0; i < symbols.length; i += WINDOWS_BATCH_MAX) chunks.push(symbols.slice(i, i + WINDOWS_BATCH_MAX));
    // Each chunk settles on its own: a failed chunk's symbols come back as error rows, so one failed request
    // is a partial clock, not a blank one. All failed is a failed read.
    const settled = await Promise.allSettled(chunks.map(read));
    const pages = settled.filter((r) => r.status === 'fulfilled').map((r) => r.value);
    if (!chunks.length) return { results: [] };
    if (!pages.length) throw settled[0].reason;
    if (pages.some((p) => !Array.isArray(p?.results))) return pages[0]; // no batch form: the caller falls back
    const results = settled.flatMap((r, i) => (r.status === 'fulfilled'
      ? r.value.results
      : chunks[i].map((symbol) => ({ symbol, requested: symbol, low: null, high: null, error: 'Failed to read cycle windows' }))));
    return { ...pages[0], results };
  },

  getWindows: async ({ symbol, timeframe, side, history, depth } = {}) => {
    if (!symbol || !timeframe) {
      throw new Error('cyclesAPI.getWindows: symbol and timeframe are required');
    }
    const params = new URLSearchParams();
    params.append('symbol', symbol);
    params.append('timeframe', toCanonicalTimeframe(timeframe));
    if (side) {
      params.append('side', side);
    }
    if (Number.isInteger(history) && history > 0) {
      params.append('history', String(history));
    }
    // DWLF-416: each window's derived price band.
    if (depth) {
      params.append('depth', '1');
    }
    const response = await apiClient.get(`/cycles/windows?${params.toString()}`);
    return response.data;
  },
};

export default cyclesAPI;
